// Table of contents with scroll-spy.
// Builds the sidebar TOC from the article's H2/H3 headings and
// highlights the section currently being read. Runs only on the
// reading page (where .gh-toc and .gh-content both exist).

function slugify(text) {
    return text
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, "")
        .replace(/[\s_-]+/g, "-")
        .replace(/^-+|-+$/g, "");
}

export default function toc() {
    const nav = document.querySelector(".gh-toc");
    const content = document.querySelector(".gh-content");
    if (!nav || !content) return;

    const list = nav.querySelector(".gh-toc-list");
    const headings = Array.from(content.querySelectorAll("h2, h3"));
    if (!list || headings.length === 0) return;

    // Indent H3s only when H2s are also present — a single-level TOC
    // (all H3s, common in shorter posts) should sit flush left.
    const mixedLevels =
        headings.some((h) => h.tagName === "H2") &&
        headings.some((h) => h.tagName === "H3");

    // Build TOC entries, assigning ids to any headings that lack them.
    const used = new Set();
    const map = new Map(); // id -> <li>

    headings.forEach((heading) => {
        let id = heading.id;
        if (!id) {
            let base = slugify(heading.textContent) || "section";
            id = base;
            let n = 2;
            while (used.has(id) || document.getElementById(id)) {
                id = `${base}-${n++}`;
            }
            heading.id = id;
        }
        used.add(id);

        const li = document.createElement("li");
        li.className =
            "gh-toc-item" +
            (mixedLevels && heading.tagName === "H3" ? " gh-toc-sub" : "");

        const a = document.createElement("a");
        a.href = `#${id}`;

        const text = document.createElement("span");
        text.className = "gh-toc-text";
        text.textContent = heading.textContent;

        const leader = document.createElement("span");
        leader.className = "gh-toc-leader";

        const page = document.createElement("span");
        page.className = "gh-toc-page";

        a.append(text, leader, page);
        li.appendChild(a);
        list.appendChild(li);
        map.set(id, li);
    });

    // Book-style "page" numbers: treat every PAGE_HEIGHT px of article as
    // one printed page. Fake, but honest about relative position.
    const PAGE_HEIGHT = 1200;
    const numberPages = () => {
        const contentTop = content.getBoundingClientRect().top + window.scrollY;
        let prev = 1;
        headings.forEach((heading) => {
            const y = heading.getBoundingClientRect().top + window.scrollY;
            const n = Math.max(prev, Math.floor((y - contentTop) / PAGE_HEIGHT) + 1);
            prev = n;
            const li = map.get(heading.id);
            const el = li && li.querySelector(".gh-toc-page");
            if (el) el.textContent = n;
        });
    };
    numberPages();
    // Images loading later shift offsets — renumber once everything settles.
    window.addEventListener("load", numberPages);

    nav.hidden = false;

    // Scroll-spy: mark the heading nearest the top of the viewport as active.
    let activeId = null;
    const setActive = (id) => {
        if (id === activeId) return;
        if (activeId && map.get(activeId)) {
            map.get(activeId).classList.remove("is-active");
        }
        activeId = id;
        if (id && map.get(id)) {
            map.get(id).classList.add("is-active");
        }
    };

    const visible = new Set();
    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) visible.add(entry.target);
                else visible.delete(entry.target);
            });

            if (visible.size > 0) {
                // Topmost currently-visible heading wins.
                const topmost = Array.from(visible).sort(
                    (a, b) =>
                        a.getBoundingClientRect().top -
                        b.getBoundingClientRect().top
                )[0];
                setActive(topmost.id);
            } else {
                // Between headings: keep the last heading scrolled past.
                let last = null;
                for (const heading of headings) {
                    if (heading.getBoundingClientRect().top < 120) {
                        last = heading;
                    } else break;
                }
                if (last) setActive(last.id);
            }
        },
        { rootMargin: "-80px 0px -70% 0px", threshold: 0 }
    );

    headings.forEach((h) => observer.observe(h));
}
